import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Field, HStack, Text, VStack, useBreakpointValue } from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';
import { BannerAvatarEditor } from '../BannerAvatarEditor';
import { RiftboundAlertDialog } from '../ui/alert-dialog';
import { RiftboundButton } from '../ui/button';
import { RiftboundIconButton } from '../ui/icon-button';
import { RiftboundInput } from '../ui/input';
import { RiftboundBottomSheet, SHEET_Z_DECK_DIALOG } from '../ui/bottom-sheet';
import { DEV_CONTROLS_Z, isDevRuntime } from '../../constants/devControls';
import { useAuth } from '../../hooks/useAuth';
import { useModals } from '../../hooks/useModals';
import {
  getGuestScanPrompt,
  subscribeGuestScanPrompt,
} from '../../scan/guestScanAccountPrompt';
import { SlugText } from '../SlugText';
import { playerProfilePath } from '../../utils/profilePaths';
import {
  isPublicSlugAvailable,
  suggestClaimablePlayerHandle,
} from '../../utils/publicCollectionSync';
import { isValidPlayerHandle, PLAYER_HANDLE_HINT } from '../../utils/playerHandle';
import {
  registerProfileSetupOpener,
  shouldOfferProfileSetup,
} from './profileSetupPrompt';

const SHOW_DELAY_MS = 600;
const HANDLE_CHECK_MS = 300;

const LATER_PROFILE_FIELDS = [
  { label: 'Bio', section: 'profile' },
  { label: 'Country', section: 'country' },
  { label: 'Banner', section: 'profile' },
  { label: 'Social links', section: 'profile' },
] as const;

type HandleAvailability = 'idle' | 'checking' | 'available' | 'taken';

function SetupDots({ step }: { step: 0 | 1 }) {
  return (
    <HStack justify="center" gap={1.5} aria-label={`Step ${step + 1} of 2`}>
      {[0, 1].map((index) => (
        <Box
          key={index}
          h="6px"
          w={index === step ? '16px' : '6px'}
          borderRadius="full"
          bg={index === step ? 'accent' : 'border'}
        />
      ))}
    </HStack>
  );
}

/**
 * One-time prompt after signup: claim a public handle and profile photo.
 * Desktop uses the shared alert dialog; mobile uses its bottom-sheet placement.
 */
export function NewUserProfilePromptHost() {
  const { user } = useAuth();
  const { signInOpen } = useModals();
  const location = useLocation();
  const [guestActive, setGuestActive] = useState(
    () => getGuestScanPrompt().kind != null || getGuestScanPrompt().pendingAdd != null,
  );
  const [forced, setForced] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(
    () =>
      subscribeGuestScanPrompt(() => {
        const prompt = getGuestScanPrompt();
        setGuestActive(prompt.kind != null || prompt.pendingAdd != null);
      }),
    [],
  );

  const openForced = useCallback(() => setForced(true), []);
  useEffect(() => {
    registerProfileSetupOpener(openForced);
    return () => registerProfileSetupOpener(null);
  }, [openForced]);

  const eligible = Boolean(
    user &&
      shouldOfferProfileSetup({
        profileSetupPending: user.profileSetupPending,
        signInOpen,
        pathname: location.pathname,
        guestPromptActive: guestActive,
      }),
  );

  useEffect(() => {
    if (!eligible) {
      setReady(false);
      return;
    }
    const id = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [eligible, user?.uid]);

  if (!user || (!forced && !ready)) return null;

  return (
    <NewUserProfileDialog
      onFinished={() => {
        setForced(false);
        setReady(false);
      }}
    />
  );
}

function NewUserProfileDialog({ onFinished }: { onFinished: () => void }) {
  const { user, updatePlayerHandle, updateDisplayName, updateAvatar, getDisplayImage, dismissProfileSetup } = useAuth();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const nameRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<0 | 1>(0);
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [handleAvailability, setHandleAvailability] = useState<HandleAvailability>('idle');
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const handleEdited = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    const current = userRef.current;
    if (!current) return;
    handleEdited.current = false;
    setDisplayName(current.displayName ?? '');
    setStep(0);
    setHandle(
      suggestClaimablePlayerHandle({
        displayName: current.displayName,
        email: current.email,
        autoSlug: current.slug,
      }),
    );
    setFormError(null);
    // Reseed only when the account changes. Avatar saves must not wipe the handle field.
  }, [user?.uid]);

  useEffect(() => {
    if (handleEdited.current) return;
    const current = userRef.current;
    if (!current || !displayName.trim()) return;
    setHandle(
      suggestClaimablePlayerHandle({
        displayName,
        email: current.email,
        autoSlug: current.slug,
      }),
    );
  }, [displayName]);

  useEffect(() => {
    const current = userRef.current;
    const trimmed = handle.trim().toLowerCase();
    if (!current || !trimmed || !isValidPlayerHandle(trimmed)) {
      setHandleAvailability('idle');
      return;
    }
    if (trimmed === current.slug) {
      setHandleAvailability('available');
      return;
    }
    let cancelled = false;
    setHandleAvailability('checking');
    const id = window.setTimeout(() => {
      void isPublicSlugAvailable(trimmed, current.uid)
        .then((available) => {
          if (!cancelled) setHandleAvailability(available ? 'available' : 'taken');
        })
        .catch(() => {
          if (!cancelled) setHandleAvailability('idle');
        });
    }, HANDLE_CHECK_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [handle]);

  const finish = useCallback(
    async (next?: () => void) => {
      if (busy.current) return;
      busy.current = true;
      setSaving(true);
      try {
        await dismissProfileSetup();
        onFinished();
        next?.();
      } finally {
        busy.current = false;
        setSaving(false);
      }
    },
    [dismissProfileSetup, onFinished],
  );

  const onSave = useCallback(async () => {
    if (!user || busy.current) return;
    const name = displayName.trim();
    const trimmed = handle.trim().toLowerCase();
    if (!name) {
      setFormError('Add a display name.');
      return;
    }
    if (!isValidPlayerHandle(trimmed)) {
      setFormError(PLAYER_HANDLE_HINT);
      return;
    }
    if (handleAvailability === 'taken') {
      setFormError('That handle is taken.');
      return;
    }
    busy.current = true;
    setSaving(true);
    setFormError(null);
    try {
      if (name !== (user.displayName ?? '')) await updateDisplayName(name);
      if (trimmed !== user.slug) await updatePlayerHandle(trimmed);
      await dismissProfileSetup();
      onFinished();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save profile.');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }, [dismissProfileSetup, displayName, handle, handleAvailability, onFinished, updateDisplayName, updatePlayerHandle, user]);

  useEffect(() => {
    if (step !== 1) return;
    nameRef.current?.focus();
  }, [step]);

  if (!user || isMobile === undefined) return null;

  const publicPath = playerProfilePath(handle);
  const isPhotoStep = step === 0;
  const hasPhoto = Boolean(getDisplayImage());
  const title = isPhotoStep ? (hasPhoto ? 'Looking good' : 'Add a photo') : 'Your public name';
  const description = isPhotoStep
    ? hasPhoto
      ? 'This is the photo other players will see.'
      : 'Players see this on your profile. You can change it later.'
    : 'Your display name and handle update as you type.';
  const handleStatusText =
    handleAvailability === 'checking'
      ? 'Checking…'
      : handleAvailability === 'available'
        ? 'Available'
        : handleAvailability === 'taken'
          ? 'That handle is taken.'
          : null;

  const dismiss = () => void finish();
  const skipPhoto = () => {
    setFormError(null);
    setStep(1);
  };
  const goBack = () => {
    setFormError(null);
    setStep(0);
  };
  const confirm = () => {
    if (isPhotoStep) {
      setStep(1);
      return;
    }
    void onSave();
  };
  const openSettings = (section: string) => {
    void finish(() => navigate(section === 'profile' ? '/profile' : `/profile/${section}`));
  };

  const stepBody = (
    <VStack align="center" gap={4} w="full" pt={1}>
      <SetupDots step={step} />
      {isPhotoStep ? (
        <BannerAvatarEditor
          hideBanner
          centered
          name={displayName || user.displayName || 'Player'}
          avatarUrl={user.avatarUrl}
          displayAvatarUrl={getDisplayImage()}
          bannerUrl={null}
          onAvatarChange={updateAvatar}
          onBannerChange={() => {}}
          removeAvatarLabel="Use account photo"
          disabled={saving}
        />
      ) : (
        <VStack align="center" gap={3} w="full">
          <VStack gap={0} align="center">
            <Text fontWeight="semibold" fontSize="xl" textAlign="center" truncate maxW="full">
              {displayName.trim() || 'Your name'}
            </Text>
            {handle.trim() ? (
              <SlugText slug={handle.trim().toLowerCase()} color="accent" fontWeight="semibold" />
            ) : null}
          </VStack>
          <Field.Root w="full">
            <Field.Label fontWeight="medium" alignSelf="start">Display name</Field.Label>
            <RiftboundInput
              ref={nameRef}
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.target.value);
                setFormError(null);
              }}
              placeholder="Your name"
              w="full"
            />
          </Field.Root>
          <Field.Root invalid={Boolean(formError)} w="full">
            <Field.Label fontWeight="medium" alignSelf="start">Player handle</Field.Label>
            <RiftboundInput
              value={handle}
              onChange={(event) => {
                handleEdited.current = true;
                setHandle(event.target.value.toLowerCase());
                setFormError(null);
              }}
              placeholder="your-handle"
              w="full"
              fontFamily="mono"
            />
            <Field.HelperText textAlign="start">
              {handle.trim() && !isValidPlayerHandle(handle) ? (
                PLAYER_HANDLE_HINT
              ) : publicPath ? (
                <>
                  Public URL{' '}
                  <Text as="span" fontFamily="mono">
                    {publicPath}
                  </Text>
                </>
              ) : (
                PLAYER_HANDLE_HINT
              )}
            </Field.HelperText>
            {handleStatusText ? (
              <Text
                fontSize="xs"
                color={handleAvailability === 'taken' ? 'danger' : 'textMuted'}
                textAlign="start"
                w="full"
              >
                {handleStatusText}
              </Text>
            ) : null}
          </Field.Root>
          {!isPhotoStep && formError ? (
            <Text fontSize="sm" color="danger" textAlign="center">
              {formError}
            </Text>
          ) : null}
          <VStack gap={1} align="center">
            <Text fontSize="xs" color="textMuted" textAlign="center">
              Also on your profile
            </Text>
              <HStack gap={2} wrap="wrap" justify="center">
                {LATER_PROFILE_FIELDS.map((field) => (
                  <RiftboundButton
                    key={field.label}
                    size="xs"
                    treatment="secondary"
                    borderRadius="full"
                    disabled={saving}
                    onClick={() => openSettings(field.section)}
                  >
                    {field.label}
                  </RiftboundButton>
                ))}
              </HStack>
          </VStack>
          <RiftboundButton
            treatment="ghost"
            size="sm"
            onClick={() => openSettings('profile')}
            disabled={saving}
          >
            Account settings
          </RiftboundButton>
        </VStack>
      )}
    </VStack>
  );

  const actions = (
    <VStack w="full" gap={2}>
      <RiftboundButton
        treatment="primary"
        w="full"
        minH="44px"
        loading={!isPhotoStep && (saving || handleAvailability === 'checking')}
        disabled={saving || (!isPhotoStep && (handleAvailability === 'checking' || handleAvailability === 'taken'))}
        onClick={confirm}
      >
        {isPhotoStep ? 'Next' : 'Save'}
      </RiftboundButton>
      {isPhotoStep && !hasPhoto ? (
        <RiftboundButton treatment="secondary" w="full" minH="44px" disabled={saving} onClick={skipPhoto}>
          Skip
        </RiftboundButton>
      ) : null}
      {isPhotoStep ? null : (
        <RiftboundButton treatment="secondary" w="full" minH="44px" disabled={saving} onClick={goBack}>
          Back
        </RiftboundButton>
      )}
      {isPhotoStep ? null : (
        <RiftboundButton treatment="secondary" w="full" minH="44px" disabled={saving} onClick={dismiss}>
          Not now
        </RiftboundButton>
      )}
    </VStack>
  );

  if (isMobile) {
    return (
      <RiftboundBottomSheet
        isOpen
        onClose={dismiss}
        detent="content-height"
        elevated
        zIndex={isDevRuntime() ? DEV_CONTROLS_Z + 2 : SHEET_Z_DECK_DIALOG + 20}
        header={
          <HStack justify="space-between" align="center" w="full" px={1}>
            <RiftboundIconButton
              aria-label="Close"
              variant="ghost"
              size="sm"
              onClick={dismiss}
              disabled={saving}
            >
              <MdClose size={22} />
            </RiftboundIconButton>
            <Text fontFamily="heading" fontWeight="semibold" fontSize="lg" textAlign="center" truncate>
              {title}
            </Text>
            <Box w="44px" flexShrink={0} aria-hidden />
          </HStack>
        }
      >
        <VStack align="center" gap={4} px={5} pb={4} w="full">
          <Text fontSize="sm" color="textMuted" textAlign="center">
            {description}
          </Text>
          {stepBody}
          {actions}
        </VStack>
      </RiftboundBottomSheet>
    );
  }

  return (
    <RiftboundAlertDialog
      open
      centered
      size="md"
      maxW="420px"
      title={title}
      description={description}
      confirmLabel={isPhotoStep ? 'Next' : 'Save'}
      confirmTreatment="primary"
      secondaryLabel={isPhotoStep ? undefined : 'Back'}
      secondaryTreatment="secondary"
      showCancel={!isPhotoStep || !hasPhoto}
      cancelLabel={isPhotoStep ? 'Skip' : 'Not now'}
      closeOnInteractOutside
      zIndex={isDevRuntime() ? DEV_CONTROLS_Z + 2 : undefined}
      autoFocusCancel={false}
      loading={saving}
      onClose={dismiss}
      onCancel={isPhotoStep ? skipPhoto : dismiss}
      onSecondary={goBack}
      onConfirm={confirm}
    >
      {stepBody}
    </RiftboundAlertDialog>
  );
}
